import {
  BodyText,
  Button,
  CheckIcon,
  CloseIcon,
  File,
  Heading,
  IconList,
  Input,
  Link,
  Modal,
  PrimitiveText,
  UploadIcon,
} from '@farol-ds/react'
import { COMPONENT_CLASS, COMPONENT_NAME } from '@jusbrasil-web/shared-ga4-events'
import { Image } from '@jusbrasil-web/shared-next-image'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { ChangeEvent, useEffect, useRef, useState } from 'react'

import identityPhotoSrc from '../../../assets/id-document-validation/identity-photo.svg'
import {
  COMPONENT_RENDER_EVENT_PROPS,
  GA4EventHandlers,
  INPUT_START_EVENT_PROPS,
} from '../../../hooks'
import { fileToBase64 } from '../../../utils'
import { DropFileZone } from '../components/drop-file-zone'
import { ACCEPTED_IMAGE_INPUT, IMAGE_MAX_SIZE_BYTES, PRIVACY_POLICY_URL } from '../constants'
import { useIdDocumentValidationContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

interface ValidateDocumentProps {
  gA4EventHandlers?: GA4EventHandlers
}

export function ValidateDocument(props: ValidateDocumentProps) {
  const { gA4EventHandlers } = props
  const { setCurrentStep, sharedData, setSharedData, eventData, viewer, texts } =
    useIdDocumentValidationContext()

  const [file, setFile] = useState<File | null>(null)
  const [isFileValid, setIsFileValid] = useState(true)
  const inputRef = useRef<HTMLInputElement>(null)

  const isSubmitDisabled = !file
  const pid = viewer?.pid

  const title = texts?.documentUpload?.title ?? 'Foto do seu documento'
  const description =
    texts?.documentUpload?.description ??
    'Tire uma foto da parte da frente do seu documento, contendo o número do CPF.'

  const resetFile = () => {
    setFile(null)
    setIsFileValid(true)
  }

  const isValidImageFile = (selectedFile: File) => {
    if (!selectedFile.type.startsWith('image/')) {
      return false
    }

    if (selectedFile.size > IMAGE_MAX_SIZE_BYTES) {
      return false
    }

    return true
  }

  const handleFileDropped = (droppedFile: File) => {
    if (!isValidImageFile(droppedFile)) {
      setIsFileValid(false)
      return
    }

    setFile(droppedFile)
    setIsFileValid(true)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) {
      return
    }

    if (!isValidImageFile(selectedFile)) {
      setIsFileValid(false)
      return
    }

    setFile(selectedFile)
    setIsFileValid(true)
    gA4EventHandlers?.sendInputStart(
      INPUT_START_EVENT_PROPS.VALIDATE_IDENTITY_DOCUMENT_FILE_UPLOAD_CPF_VALIDATION_FORM_ATTACHMENT
    )
  }

  const storeDocumentInSharedData = async (selectedFile: File) => {
    const fileBase64 = await fileToBase64(selectedFile)
    const cpf = sharedData.cpf || viewer?.cpf
    const birthDate =
      sharedData.birthDate ?? (viewer?.birthDate ? new Date(viewer.birthDate) : undefined)

    if (!cpf || !birthDate) {
      return
    }

    setSharedData({
      ...sharedData,
      cpf,
      birthDate,
      documentFile: selectedFile,
      documentData: fileBase64,
      documentName: selectedFile.name,
    })
  }

  const goToDocumentPreview = async (selectedFile: File) => {
    await storeDocumentInSharedData(selectedFile)
    setCurrentStep(Steps.DOCUMENT_PREVIEW)
  }

  const handleDeskSubmit = async () => {
    if (!file || !file.type.startsWith('image/')) {
      setIsFileValid(false)
      setFile(null)
      return
    }

    sendMetrics('UserAccount.IdValidationDocumentSubmitted', `${pid}`, { ...eventData })
    await storeDocumentInSharedData(file)
    setCurrentStep(Steps.VALIDATING_DOCUMENT)
  }

  const triggerFileInput = (selectType: string) => {
    inputRef?.current?.click()
    sendMetrics('UserAccount.IdValidationDocumentUploadClicked', `${pid}`, {
      ...eventData,
      metadata: {
        ...eventData?.metadata,
        select_type: selectType,
      },
    })
  }

  const handleMobileSubmit = async () => {
    if (!file) {
      triggerFileInput('submitButton')
      return
    }

    await goToDocumentPreview(file)
  }

  const handleMobileImageBoxClick = () => {
    triggerFileInput('imageBox')
  }

  useEffect(() => {
    sendMetrics('UserAccount.IdValidationDocumentViewed', `${pid}`, { ...eventData })
    gA4EventHandlers?.sendComponentRender({
      componentClass: COMPONENT_CLASS.CPF_VALIDATION_FORM_ATTACHMENT_ACCESS,
      componentName: COMPONENT_NAME.DOCUMENT_VALIDATION,
      renders:
        COMPONENT_RENDER_EVENT_PROPS.VALIDATE_IDENTITY_DOCUMENT_CPF_VALIDATION_FORM_ATTACHMENT,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Modal.Header>
        <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
        <Modal.HeaderDescription>{description}</Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body data-testid="validate-document-step">
        <Input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_IMAGE_INPUT}
          onChange={handleFileChange}
          className={styles.hiddenFileInput}
          aria-hidden
          data-testid="validate-document-input-file"
        />
        <button
          type="button"
          onClick={handleMobileImageBoxClick}
          className={styles.mobileFileUpload}
        >
          <Image alt="Documento com foto" src={identityPhotoSrc} />
          <BodyText>
            Documentos de identificação com foto válidos: <strong>RG</strong> ou{' '}
            <strong>CNH</strong>
          </BodyText>
        </button>
        <div className={styles.deskFileUpload}>
          {file ? (
            <File rightIcon={<CloseIcon onClick={resetFile} />}>
              <File.Content>
                <File.Label>{file.name}</File.Label>
              </File.Content>
            </File>
          ) : (
            <DropFileZone
              className={styles.dropArea}
              isFileValid={isFileValid}
              onFileDropped={handleFileDropped}
              onDropZoneClick={() => triggerFileInput('selectDocument')}
            >
              <div className={styles.dropAreaIcon}>
                <UploadIcon />
              </div>
              <Heading size="sm">
                Arraste e solte o arquivo aqui ou{' '}
                <PrimitiveText className={styles.dropAreaHighlight}>
                  selecione o arquivo
                </PrimitiveText>
              </Heading>
            </DropFileZone>
          )}
        </div>

        <IconList size="md" className={styles.deskHintList}>
          <IconList.Item icon={<CheckIcon />}>O documento precisa estar legível</IconList.Item>
          <IconList.Item
            icon={isFileValid ? <CheckIcon /> : <CloseIcon className={styles.errorIcon} />}
          >
            O arquivo deve ser no formato JPG ou PNG e ter até 10mb
          </IconList.Item>
        </IconList>

        <IconList size="md" className={styles.mobileHintList}>
          <IconList.Item icon={<CheckIcon />}>
            Seu CPF deve estar visível no documento
          </IconList.Item>
          <IconList.Item icon={<CheckIcon />}>Tire do plástico</IconList.Item>
          <IconList.Item icon={<CheckIcon />}>Esteja em um local iluminado </IconList.Item>
          <IconList.Item
            icon={isFileValid ? <CheckIcon /> : <CloseIcon className={styles.errorIcon} />}
          >
            O arquivo deve ser no formato JPG ou PNG e ter até 10mb
          </IconList.Item>
        </IconList>

        <BodyText>
          Ao avançar, você concorda que nós (e nossos prestadores de serviço) podemos armazenar e
          utilizar a foto de seu documento para fins de identificação. Saiba mais na nossa{' '}
          <Link type="secondary" href={PRIVACY_POLICY_URL}>
            Política de Privacidade
          </Link>
          .
        </BodyText>
      </Modal.Body>

      <Modal.Footer>
        <Button
          data-testid="validate-document-mobile-submit"
          kind="primary"
          onClick={handleMobileSubmit}
          className={styles.mobileSubmitBtn}
        >
          Avançar
        </Button>
        <Button
          data-testid="validate-document-desk-submit"
          kind="primary"
          onClick={handleDeskSubmit}
          disabled={isSubmitDisabled}
          className={styles.deskSubmitBtn}
        >
          Avançar
        </Button>
      </Modal.Footer>
    </>
  )
}
